import { FunctionComponent, useEffect, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './confirmRequest.module.scss'
import { ArrowForward, Check, Close } from '@mui/icons-material'
import { Daycare } from '@/types/Daycare'
import ConfirmRegularRequest from '../../planning/Dialog/regularRequest/confirmRegularRequest/confirmRegularRequest.tsx'
import { ChildRequestSlot, ChildWithParents } from '@/types/Child.ts'
import { OccasionalSlotsPlaces, RegularCancellationRequest, RequestSlot } from '@/types/Slots.ts'
import ConfirmRequestSlot from '../../planning/Dialog/requestSlot/confirmRequestSlot/confirmRequestSlot.tsx'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos.ts'

// No-op function used as a placeholder callback when the actual callback is handled by closeParentDialog
const noop: () => void = (): void => undefined

interface ConfirmRequestProps {
    open: boolean
    handleClose: () => void
    acceptRequest: boolean
    selectedRequests: (RegularCancellationRequest | RequestSlot)[]
    currentDaycare: Daycare
    childs: ChildWithParents[]
    places: OccasionalSlotsPlaces[]
    refetchData: () => Promise<void>
}

const ConfirmRequest: FunctionComponent<ConfirmRequestProps> = ({
    open,
    handleClose,
    acceptRequest,
    selectedRequests,
    currentDaycare,
    childs,
    places,
    refetchData
}: ConfirmRequestProps) => {
    const { t } = useTranslation()

    const { isRpeOrLaep } = useCurrentDaycareInfos()

    function isRequestSlot(item: RequestSlot | RegularCancellationRequest): item is RequestSlot {
        return 'daycareId' in item
    }

    function isRegularCancellation(item: RequestSlot | RegularCancellationRequest): item is RegularCancellationRequest {
        return 'reason' in item
    }

    const [numberOfUpdates, setNumberOfUpdates] = useState<RegularCancellationRequest[]>(
        selectedRequests
            .filter(
                (request: RegularCancellationRequest | RequestSlot): boolean =>
                    isRegularCancellation(request) &&
                    (request.start !== request.regularSlotStart || request.end !== request.regularSlotEnd)
            )
            .map(
                (request: RegularCancellationRequest | RequestSlot): RegularCancellationRequest =>
                    request as RegularCancellationRequest
            )
    )
    const [numberOfAbsences, setNumberOfAbsences] = useState<RegularCancellationRequest[]>(
        selectedRequests
            .filter(
                (request: RegularCancellationRequest | RequestSlot): boolean =>
                    isRegularCancellation(request) &&
                    request.start === request.regularSlotStart &&
                    request.end === request.regularSlotEnd
            )
            .map(
                (request: RegularCancellationRequest | RequestSlot): RegularCancellationRequest =>
                    request as RegularCancellationRequest
            )
    )
    const [numberOfSlotsRequests, setNumberOfSlotsRequests] = useState<RequestSlot[]>(
        selectedRequests
            .filter((request: RegularCancellationRequest | RequestSlot): boolean => isRequestSlot(request))
            .map((request: RegularCancellationRequest | RequestSlot): RequestSlot => request as RequestSlot)
    )

    const [isShowRegularRequest, setIsShowRegularRequest] = useState<boolean>(false)
    const [isShowRequest, setIsShowRequest] = useState<boolean>(false)

    const hasRegularRequests: boolean = [...numberOfAbsences, ...numberOfUpdates].length >= 1
    const hasSlotRequests: boolean = numberOfSlotsRequests.length >= 1

    function handleOpenDialog(): void {
        if (hasRegularRequests) {
            setIsShowRegularRequest(true)
        } else if (hasSlotRequests) {
            setIsShowRequest(true)
        }
    }

    function handleCloseRegularRequest(): void {
        setIsShowRegularRequest(false)
        if (hasSlotRequests) {
            setIsShowRequest(true)
        } else {
            handleClose()
            refetchData()
        }
    }

    function handleCloseSlotRequest(refetch: boolean): void {
        setIsShowRequest(false)
        handleClose()

        if (refetch) refetchData()
    }

    useEffect(() => {
        setNumberOfUpdates(
            selectedRequests
                .filter(
                    (request: RegularCancellationRequest | RequestSlot): boolean =>
                        isRegularCancellation(request) &&
                        (request.start !== request.regularSlotStart || request.end !== request.regularSlotEnd)
                )
                .map(
                    (request: RegularCancellationRequest | RequestSlot): RegularCancellationRequest =>
                        request as RegularCancellationRequest
                )
        )
        setNumberOfAbsences(
            selectedRequests
                .filter(
                    (request: RegularCancellationRequest | RequestSlot): boolean =>
                        isRegularCancellation(request) &&
                        request.start === request.regularSlotStart &&
                        request.end === request.regularSlotEnd
                )
                .map(
                    (request: RegularCancellationRequest | RequestSlot): RegularCancellationRequest =>
                        request as RegularCancellationRequest
                )
        )
        setNumberOfSlotsRequests(
            selectedRequests
                .filter((request: RegularCancellationRequest | RequestSlot): boolean => isRequestSlot(request))
                .map((request: RegularCancellationRequest | RequestSlot): RequestSlot => request as RequestSlot)
        )
    }, [selectedRequests])

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={acceptRequest ? t('acceptRequests') : t('refuseRequests')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>{acceptRequest ? t('acceptRequestsMessage') : t('refuseRequestsMessage')}</p>
                <ul className={styles.List}>
                    {numberOfUpdates.length > 0 && (
                        <li className={styles.ListItem}>
                            <ArrowForward className={styles.Icon} />
                            {numberOfUpdates.length}{' '}
                            {numberOfUpdates.length === 1
                                ? t('modification').toLowerCase()
                                : t('modifications').toLowerCase()}
                        </li>
                    )}
                    {numberOfAbsences.length > 0 && (
                        <li className={styles.ListItem}>
                            <ArrowForward className={styles.Icon} />
                            {numberOfAbsences.length}{' '}
                            {numberOfAbsences.length === 1 ? t('absence').toLowerCase() : t('absences').toLowerCase()}
                        </li>
                    )}
                    {numberOfSlotsRequests.length > 0 && (
                        <li className={styles.ListItem}>
                            <ArrowForward className={styles.Icon} />
                            {numberOfSlotsRequests.length}{' '}
                            {numberOfSlotsRequests.length === 1 ? t('slot').toLowerCase() : t('slots').toLowerCase()}
                        </li>
                    )}
                </ul>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Check />}
                        onClick={handleOpenDialog}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
            {isShowRegularRequest && hasRegularRequests && (
                <ConfirmRegularRequest
                    open={isShowRegularRequest}
                    handleClose={() => handleCloseSlotRequest(false)}
                    regularRequests={[...numberOfAbsences, ...numberOfUpdates]}
                    childs={childs}
                    accepted={acceptRequest}
                    refetchData={noop}
                    closeParentDialog={handleCloseRegularRequest}
                    isRpeOrLaep={isRpeOrLaep}
                    places={places}
                />
            )}
            {isShowRequest && hasSlotRequests && (
                <ConfirmRequestSlot
                    open={isShowRequest}
                    handleClose={() => handleCloseSlotRequest(false)}
                    accepted={acceptRequest}
                    currentDaycare={currentDaycare}
                    refetchData={noop}
                    closeParentDialog={() => handleCloseSlotRequest(true)}
                    childRequestSlots={numberOfSlotsRequests.map((req: RequestSlot): ChildRequestSlot => {
                        return {
                            slotId: req.id,
                            childId: req.childId,
                            childName: req.childName,
                            childComplement: req.childComplement,
                            date: req.date,
                            start: req.start,
                            end: req.end
                        }
                    })}
                />
            )}
        </Dialog>
    )
}

export default ConfirmRequest
